import React, { useState, useEffect } from 'react';
import { X, Edit2 } from 'lucide-react';
import api from '../../services/api';
import DataTable from '../Tables/DataTable';
import ProjectForm from '../Forms/ProjectForm';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/currency';

interface DeveloperProjectsModalProps {
    isOpen: boolean;
    onClose: () => void;
    developerId: string;
    developerName: string;
}

const DeveloperProjectsModal: React.FC<DeveloperProjectsModalProps> = ({ isOpen, onClose, developerId, developerName }) => {
    const { currency } = useAuth();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingProject, setEditingProject] = useState<any>(null);

    useEffect(() => {
        const fetchProjects = async () => {
            if (!isOpen || !developerId) return;
            setLoading(true);
            try {
                const res = await api.get(`/projects?developerId=${developerId}`);
                setProjects(res.data);
            } catch (err) {
                console.error('Failed to fetch developer projects', err);
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [isOpen, developerId]);

    const handleProjectSuccess = async () => {
        setEditingProject(null);
        setLoading(true);
        try {
            const res = await api.get(`/projects?developerId=${developerId}`);
            setProjects(res.data);
        } catch (err) {
            console.error('Failed to fetch developer projects', err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const columns = [
        { header: 'Project Name', accessor: 'projectName' },
        {
            header: 'Assigned Amount',
            accessor: (item: any) => {
                const assignment = item.developers?.find((d: any) => d.developerId === developerId);
                return formatCurrency(assignment?.amount || 0, currency);
            }
        },
        {
            header: 'Paid Amount',
            accessor: (item: any) => {
                const assignment = item.developers?.find((d: any) => d.developerId === developerId);
                const paid = assignment?.installments?.reduce((acc: number, inst: any) => {
                    return inst.status === 'Paid' ? acc + inst.amount : acc;
                }, 0) || 0;
                return formatCurrency(paid, currency);
            }
        },
        {
            header: 'Remaining Amount',
            accessor: (item: any) => {
                const assignment = item.developers?.find((d: any) => d.developerId === developerId);
                const totalAssigned = assignment?.amount || 0;
                const paid = assignment?.installments?.reduce((acc: number, inst: any) => {
                    return inst.status === 'Paid' ? acc + inst.amount : acc;
                }, 0) || 0;
                const remaining = totalAssigned - paid;
                return formatCurrency(remaining, currency);
            }
        }
    ];

    let totalAssignedAll = 0;
    let totalPaidAll = 0;
    let totalRemainingAll = 0;

    projects.forEach((item: any) => {
        const assignment = item.developers?.find((d: any) => d.developerId === developerId);
        const totalAssigned = assignment?.amount || 0;
        const paid = assignment?.installments?.reduce((acc: number, inst: any) => {
            return inst.status === 'Paid' ? acc + inst.amount : acc;
        }, 0) || 0;
        const remaining = totalAssigned - paid;

        totalAssignedAll += totalAssigned;
        totalPaidAll += paid;
        totalRemainingAll += remaining;
    });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-xl font-semibold text-gray-800">
                        Projects for {developerName}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <DataTable
                        data={projects}
                        columns={columns}
                        total={projects.length}
                        page={1}
                        limit={projects.length || 10}
                        onPageChange={() => { }}
                        onEdit={(project) => setEditingProject(project)}
                        loading={loading}
                    />
                </div>

                {editingProject && (
                    <ProjectForm
                        project={editingProject}
                        onClose={() => setEditingProject(null)}
                        onSuccess={handleProjectSuccess}
                    />
                )}

                <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                    <div className="flex justify-between items-center">
                        <div className="flex gap-8">
                            <div>
                                <p className="text-sm text-gray-500">Total Assigned</p>
                                <p className="text-lg font-bold text-gray-800">{formatCurrency(totalAssignedAll, currency)}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Total Paid</p>
                                <p className="text-lg font-bold text-green-600">{formatCurrency(totalPaidAll, currency)}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Total Remaining</p>
                                <p className="text-lg font-bold text-blue-600">{formatCurrency(totalRemainingAll, currency)}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition text-gray-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeveloperProjectsModal;
